/**
 * EBE parts psdt/p_car（車両検索）向けのフォーム組み立て・レスポンス整形。
 * C# GetCarParamVer2（api_version=4）に揃えた buildCarFormFields。
 *
 * 応答1件の型は {@link ./pCarApiTypes.js} の `PcarApiCarRow`。
 *
 * ## psdt/p_car POST（application/x-www-form-urlencoded）の各フィールド
 *
 * - **full_kata** / **series** … 車輌型式（`carSharyoKatashiki`）が空でなければセット（`buildCarFormFields` 実装どおり）。
 *   - ハイフン分割が **3トークン以上**: `full_kata` は先頭を除いた残りを `-` で連結、`series` は **2番目のトークン**。
 *   - **2トークン**（例 `DBA-ZRR80G`）: `full_kata` は入力全体、`series` は2番目。
 *   - **1トークン**: `full_kata` と `series` に同一文字列。
 * - **kata_sitei** … 型式指定番号。`carModelNumber` を整数化。未入力・不正は `"0"`。
 * - **ruibetu** … 類別番号。`carTypeNumber`。未入力・不正は `"0"`。
 * - **sfrm** … 車台番号末尾から取る数字（最大7桁）。`carNumber` のみから算出（C# FormatFrameno 相当）。無ければ `"0"`。
 * - **snen** … 年式（任意）。ダイアログ検索では未使用（API 絞り込みに効かないため）。
 * - **syasyu_nm** … 車種名（任意）。ダイアログ検索では未使用（API 絞り込みに効かないため）。
 * - **c_maker_cd** … EBE メーカーコード（正の整数）。`carMakerCode` が数字のみのときのみ付与。任意。
 * - **c_catalog_sya_type_seq** … カタログ車種型式連番。現ダイアログ条件では 0 のため送信しない。
 * - **api_version** … `"4"` 固定。
 *
 * ### ダイアログで使う検索条件
 * 型式指定・類別・車輌型式・車台番号（sfrm）のみ。車名・年式は EBE 側で絞り込みに効かないため UI から除外。
 * 参照: `file/EPS_Parts/OldDLLMode.cs` の `GetCarParamVer2`
 */

import { CAR_SEARCH_LIST_COLUMNS } from "./carSearchFormBridge.js";

const DEFAULT_P_CAR_PATH = "/psdt/p_car";

const s = (v) => (v == null ? "" : String(v)).trim();

/**
 * フォームの MakerCD が EBE の c_maker_cd（正の整数）ならその値。英字コード等は 0。
 * @param {unknown} carMakerCode
 */
export function parseEbeCMakerCd(carMakerCode) {
  const t = s(carMakerCode);
  if (!t || !/^\d+$/.test(t)) return 0;
  const n = parseInt(t, 10);
  return Number.isFinite(n) && n > 0 ? n : 0;
}

/**
 * p_car 検索結果一覧の列定義（{@link ./carSearchFormBridge.js} の CAR_SEARCH_LIST_COLUMNS と同一。エイリアスは後方互換用）
 * @type {{ key: string; label: string }[]}
 */
export const PCAR_LIST_COLUMNS = CAR_SEARCH_LIST_COLUMNS;

/**
 * @param {string} seriesRaw ユーザー入力の型式（例: DBA-ZRR80G）
 * @param {Object} [opt]
 * @param {number} [opt.kataSitei]
 * @param {number} [opt.ruibetu]
 * @param {number} [opt.sfrm]
 * @param {number} [opt.snen]
 * @param {string} [opt.syasyuNm]
 * @param {number} [opt.syaTypeSeq]
 * @param {string} [opt.apiVersion]
 * @returns {URLSearchParams}
 */
export function buildCarFormFields(
  seriesRaw,
  {
    kataSitei = 0,
    ruibetu = 0,
    sfrm = 0,
    snen = 0,
    syasyuNm = "",
    syaTypeSeq = 0,
    apiVersion = "4",
    cMakerCd = 0,
  } = {},
) {
  const params = new URLSearchParams();
  const series = s(seriesRaw);
  if (series) {
    const katas = series.split("-");
    let fullKata = series;
    if (katas.length > 2) {
      fullKata = katas.slice(1).join("-");
    }
    params.set("full_kata", fullKata);
    params.set("series", katas.length > 1 ? katas[1] : series);
  }
  if (syaTypeSeq) params.set("c_catalog_sya_type_seq", String(syaTypeSeq));
  if (cMakerCd && Number(cMakerCd) > 0) params.set("c_maker_cd", String(cMakerCd));
  params.set("kata_sitei", String(kataSitei));
  params.set("ruibetu", String(ruibetu));
  params.set("sfrm", String(sfrm));
  if (snen) params.set("snen", String(snen));
  params.set("syasyu_nm", syasyuNm ?? "");
  params.set("api_version", String(apiVersion));
  return params;
}

/**
 * 車台番号文字列から末尾数字（最大7桁）を sfrm 用に取り出す（C# FormatFrameno に相当）
 * @param {unknown} framenoRaw
 */
export function parseSfrmFromFrameno(framenoRaw) {
  const w = s(framenoRaw);
  if (!w) return 0;
  let dest = "";
  for (let i = w.length - 1; i >= 0; i--) {
    const ch = w[i];
    if (ch < "0" || ch > "9") break;
    dest = ch + dest;
    if (dest.length >= 7) break;
  }
  const n = parseInt(dest, 10);
  return Number.isFinite(n) ? n : 0;
}

/**
 * ダイアログ条件（carSearchFormBridge のキー）から buildCarFormFields 用を組む。
 * C# GetCarParamVer2: `series` は型式のみ。車種名は `syasyu_nm`（carModel）へ。車種名を series に送らないこと。
 * @param {Record<string, string>} criteria
 * @returns {{ seriesRaw: string; buildOptions: Record<string, string | number> }}
 */
export function dialogCriteriaToSeriesAndOptions(criteria = {}) {
  const seriesRaw = s(criteria.carSharyoKatashiki);
  const kataNum = parseInt(s(criteria.carModelNumber), 10);
  const ruiNum = parseInt(s(criteria.carTypeNumber), 10);
  const snenNum = parseInt(s(criteria.carModelYear), 10);
  return {
    seriesRaw,
    buildOptions: {
      kataSitei: Number.isFinite(kataNum) ? kataNum : 0,
      ruibetu: Number.isFinite(ruiNum) ? ruiNum : 0,
      sfrm: parseSfrmFromFrameno(criteria.carNumber),
      snen: Number.isFinite(snenNum) ? snenNum : 0,
      syasyuNm: s(criteria.carModel),
      syaTypeSeq: 0,
      apiVersion: "4",
      cMakerCd: parseEbeCMakerCd(criteria.carMakerCode),
    },
  };
}

/**
 * @param {string} carUrl 完全 URL（末尾パス含む）
 * @param {URLSearchParams} formFields
 * @param {{ timeoutMs?: number; signal?: AbortSignal }} [opt]
 */
export async function searchCarAtUrl(carUrl, formFields, { timeoutMs = 25000, signal } = {}) {
  const timeoutSignal =
    typeof AbortSignal !== "undefined" && typeof AbortSignal.timeout === "function"
      ? AbortSignal.timeout(timeoutMs)
      : undefined;
  const res = await fetch(carUrl, {
    method: "POST",
    headers: {
      "Content-Type": "application/x-www-form-urlencoded; charset=UTF-8",
    },
    body: formFields.toString(),
    signal: signal ?? timeoutSignal,
  });
  if (!res.ok) {
    throw new Error(`HTTP ${res.status} ${res.statusText}`);
  }
  return res.json();
}

/**
 * @param {string} baseUrl 例: https://api.ebe-parts.com（末尾スラッシュ可）
 */
export function resolvePcarUrl(baseUrl) {
  const b = s(baseUrl).replace(/\/$/, "");
  return `${b}${DEFAULT_P_CAR_PATH}`;
}

/**
 * p_car 1件を一覧行へ（`pcar` に生オブジェクト、一覧用は carSearch 互換のフラットキー）
 * @param {Record<string, unknown>} item
 * @returns {import("./pCarApiTypes.js").SyaryoPcarSearchRow & Record<string, string>}
 */
export function mapPcarDataItemToCarSearchRow(item = {}) {
  const child = /** @type {Record<string, unknown>} */ (item.children?.[0] ?? {});
  const merged = /** @type {Record<string, unknown>} */ ({ ...child, ...item });

  const pick = (...keys) => {
    for (const k of keys) {
      const v = merged[k];
      const t = s(v);
      if (t) return t;
    }
    return "";
  };

  const nenFromYm = (ym) => {
    const t = s(ym);
    if (!t || t.length < 4) return "";
    return t.slice(0, 4);
  };

  /** @type {import("./pCarApiTypes.js").PcarApiCarRow} */
  const pcar = /** @type {import("./pCarApiTypes.js").PcarApiCarRow} */ (/** @type {unknown} */ ({ ...merged }));

  /** @type {Record<string, string>} */
  const out = { pcar };

  const ruibetsuRaw = merged.RUIBETU ?? merged.ruibetsubango;
  const kataSiteiRaw = merged.KATA_SITEI ?? merged.katashikishitei;
  const ruibetsuStr =
    ruibetsuRaw != null && ruibetsuRaw !== ""
      ? String(ruibetsuRaw)
          .padStart(4, "0")
          .slice(-4)
      : "";
  const kataSiteiStr =
    kataSiteiRaw != null && kataSiteiRaw !== ""
      ? String(kataSiteiRaw)
          .padStart(5, "0")
          .slice(0, 5)
      : "";

  out.carMakerName = pick("MAKER_NM", "maker_meisyo", "maker_name", "maker_meisho", "car_maker_nm");
  out.carMakerCode = s(merged.C_MAKER_CD ?? merged.maker_code ?? "");
  out.carName = pick("SYASYU_NM", "shamei2", "SHASYU_NM", "car_name");
  out.carSharyoKatashiki = pick("FULL_KATA_ER", "FULL_KATA", "shatai_kata", "katashiki_full", "SYA_TYPE", "SYATYPE");
  out.carDoorNm = pick("DOOR_NM");
  out.carDisplacementNm = pick("DISPLACEMENT_NM");
  /** 検索条件の「車種」（CarName）と同義。型式記号は carSharyoKatashiki を使う */
  out.carModel = pick("SYASYU_NM", "shamei2", "SHASYU_NM", "car_name");
  out.carModelNumber = kataSiteiStr || pick("KATA_SITEI", "katashikishitei", "Katashiki");
  out.carTypeNumber = ruibetsuStr;
  out.carTransmission = pick("MISSION_NM", "GEARBOX_NM", "MISSION", "shift_hensoku");
  out.carEngineModel = pick("ENGINE_NM", "ENGINE", "gendoki_kata", "gendoki_kata2");
  out.carFuelType = pick("FUEL", "nenryo_code");
  out.carGrade = pick("CARGRADE_NM", "CARGRADE", "grade");
  out.carModelYear =
    nenFromYm(merged.SNENST ?? merged.SNEN ?? child.SNENST) ||
    nenFromYm(merged.shiki) ||
    nenFromYm(merged.SNENED);
  out.carNumber = pick("MODEL_PLATE", "frameno", "FRAME_NO", "shadai_no");
  out.carColorNo = pick("COLOR", "color", "COLOR_CD");
  out.carColorName = pick("COLOR_NM");
  out.carTrimNo = pick("TRIM_NM", "torimu", "TRIM_CD");
  out.carDrive = pick("DRIVE_NM", "kudou2", "DRIVE");
  out.carForcedInduction = s(merged.INTAKE_AND_EXHAUST_NM ?? "");
  out.carRemarkNm = pick("REMARK_NM", "REMARK", "remark_nm");
  out.carBodyNm = pick("BODY_NM", "BODY", "body_nm");
  out.carSyaType = pick("SYA_TYPE", "SYATYPE", "sya_type");

  return /** @type {import("./pCarApiTypes.js").SyaryoPcarSearchRow & Record<string, string>} */ (/** @type {unknown} */ (out));
}


/**
 * @param {{ data?: unknown[]; status?: unknown }} json
 * @param {{ criteriaCarNumber?: string }} [opt]
 * @returns {Record<string, string>[]}
 */
export function mapPcarApiPayloadToRows(json = {}, { criteriaCarNumber } = {}) {
  const arr = Array.isArray(json.data) ? json.data : [];
  const rows = arr.map((row) => mapPcarDataItemToCarSearchRow(/** @type {Record<string, unknown>} */ (row)));
  const fallback = s(criteriaCarNumber);
  if (!fallback) return rows;
  // p_car は MODEL_PLATE を返さないことが多い。旧 Falcon CarController と同様、検索条件の車台番号を補完する。
  return rows.map((row) => ({
    ...row,
    carNumber: s(row.carNumber) || fallback,
  }));
}
